/**
 * Data hook for the console's Jobs section (U12/U13): the user's workflows
 * + scheduled jobs, with the enable/disable toggle and the FR-SCHED-4 cap
 * conflict surfaced as state (the deactivate-one chooser copy). Mirrors the
 * `useUserFiles` idiom: monotonic request guard, friendly errors, reload.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { listImageJobs, type ImageJob } from '../../agents/imageJobsApi.ts';
import {
  ActiveScheduleLimitError,
  disableSchedule,
  enableSchedule,
  listSchedules,
  type JobDefinition,
} from '../../agents/jobsApi.ts';
import { listWorkflows, type WorkflowSummary } from '../../agents/workflowsApi.ts';
import { isNetworkError } from '../../lib/networkError.ts';

/** Refresh cadence while an image job is still generating (the record flips
 *  server-side when the completion consumer runs — poll, don't push). */
const IMAGE_JOB_POLL_MS = 15_000;

/** Non-terminal statuses keep the panel refreshing. The record flips to
 *  complete/failed out-of-band, so anything else means "still generating"
 *  (today `queued`; liberal so an intermediate status can't stall the poll). */
function isImageJobGenerating(job: ImageJob): boolean {
  return job.status !== 'complete' && job.status !== 'failed';
}

export interface CapConflict {
  readonly message: string;
  readonly active: readonly { jobId: string; workflowId: string }[];
}

export interface UseJobsData {
  readonly workflows: readonly WorkflowSummary[];
  readonly schedules: readonly JobDefinition[];
  /** Image-generation jobs (C7 records), newest first. Fail-soft: an
   *  image-jobs fetch error leaves this empty without failing the section. */
  readonly imageJobs: readonly ImageJob[];
  readonly loading: boolean;
  readonly error: string | null;
  /** FR-SCHED-4: set when an enable hit the active cap; cleared on change. */
  readonly capConflict: CapConflict | null;
  readonly reload: () => void;
  /** Silently refetch the image-job records — call when the Jobs panel becomes
   *  visible. No-op (no request, no loading flicker) unless some image job is
   *  still generating. */
  readonly refreshImageJobs: () => void;
  readonly toggleSchedule: (jobId: string, enabled: boolean) => Promise<void>;
  readonly clearCapConflict: () => void;
}

function friendly(err: unknown, fallback: string): string {
  if (isNetworkError(err)) {
    return 'You appear to be offline. Reconnect and try again.';
  }
  return err instanceof Error ? err.message : fallback;
}

export function useJobsData(enabled: boolean): UseJobsData {
  const [workflows, setWorkflows] = useState<readonly WorkflowSummary[]>([]);
  const [schedules, setSchedules] = useState<readonly JobDefinition[]>([]);
  const [imageJobs, setImageJobs] = useState<readonly ImageJob[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [capConflict, setCapConflict] = useState<CapConflict | null>(null);
  const reqRef = useRef(0);

  const load = useCallback(() => {
    if (!enabled) {
      return;
    }
    const req = ++reqRef.current;
    setLoading(true);
    setError(null);
    void (async () => {
      // Image jobs are additive — fetched alongside but never allowed to fail
      // the core Workflows/Schedules listing.
      const imagePromise = listImageJobs().catch((err: unknown) => {
        console.error('[useJobsData] image jobs failed', err);
        return [] as readonly ImageJob[];
      });
      try {
        const [wf, sched, images] = await Promise.all([
          listWorkflows(),
          listSchedules(),
          imagePromise,
        ]);
        if (reqRef.current === req) {
          setWorkflows(wf);
          setSchedules(sched);
          setImageJobs(images);
          setLoading(false);
        }
      } catch (err) {
        if (reqRef.current === req) {
          setError(friendly(err, 'Could not load your jobs.'));
          setLoading(false);
        }
      }
    })();
  }, [enabled]);

  useEffect(() => {
    load();
  }, [load]);

  // Latest records for the visibility-driven refresh below, which must read
  // current state without re-subscribing listeners on every poll result.
  const imageJobsRef = useRef<readonly ImageJob[]>([]);
  useEffect(() => {
    imageJobsRef.current = imageJobs;
  }, [imageJobs]);

  // Silent image-jobs refetch (no loading flicker) — a no-op unless some job
  // is still generating, so callers can fire it on any "panel became visible"
  // signal without cost when everything is terminal.
  const refreshImageJobs = useCallback(() => {
    if (!enabled || !imageJobsRef.current.some(isImageJobGenerating)) {
      return;
    }
    const req = ++reqRef.current;
    void listImageJobs()
      .then((images) => {
        if (reqRef.current === req) {
          setImageJobs(images);
        }
      })
      .catch((err: unknown) => {
        console.error('[useJobsData] image jobs refresh failed', err);
      });
  }, [enabled]);

  // While any image job is still generating, refresh its records on a timer —
  // the completion consumer flips job.json out-of-band, so the panel polls
  // until every job reaches a terminal state.
  useEffect(() => {
    if (!enabled || !imageJobs.some(isImageJobGenerating)) {
      return;
    }
    const timer = setTimeout(refreshImageJobs, IMAGE_JOB_POLL_MS);
    return () => clearTimeout(timer);
  }, [enabled, imageJobs, refreshImageJobs]);

  // Returning to the browser tab refreshes a still-generating list right away:
  // background tabs throttle setTimeout, so the poll chain can stall while the
  // user is elsewhere and show a long-finished job as "Generating…".
  useEffect(() => {
    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        refreshImageJobs();
      }
    };
    document.addEventListener('visibilitychange', onVisibilityChange);
    return () => document.removeEventListener('visibilitychange', onVisibilityChange);
  }, [refreshImageJobs]);

  const toggleSchedule = useCallback(
    async (jobId: string, next: boolean) => {
      setCapConflict(null);
      try {
        const updated = next ? await enableSchedule(jobId) : await disableSchedule(jobId);
        setSchedules((prev) => prev.map((s) => (s.jobId === jobId ? updated : s)));
      } catch (err) {
        if (err instanceof ActiveScheduleLimitError) {
          setCapConflict({ message: err.message, active: err.active });
          return;
        }
        setError(friendly(err, 'Could not update that schedule.'));
      }
    },
    [],
  );

  return {
    workflows,
    schedules,
    imageJobs,
    loading,
    error,
    capConflict,
    reload: load,
    refreshImageJobs,
    toggleSchedule,
    clearCapConflict: () => setCapConflict(null),
  };
}
